import React, { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import '../../component-styles/StripePaymentModal.css';
import API_BASE from '../../utils/api.js';
import { roundCurrency, sanitizeCurrencyInput } from '../../utils/currency.js';

export default function StripePaymentModal({
  amount,
  description,
  projectId,
  targetType, // "retainer" | "timebilling" | "invoice" | "proposal" | "purchase-order"
  targetId,
  userId,
  onSuccess,
  onClose,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [customAmount, setCustomAmount] = useState('');

  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' | 'ach'
  const [achName, setAchName] = useState('');
  const [achEmail, setAchEmail] = useState('');
  const [achStep, setAchStep] = useState('input'); // 'input' | 'mandate'
  const [pendingClientSecret, setPendingClientSecret] = useState('');
  const [pendingIntentId, setPendingIntentId] = useState('');

  const apiUrl = API_BASE;
  const token = localStorage.getItem('idToken');
  const effectiveUserId = userId || localStorage.getItem('userId') || null;
  const remainingBalance = roundCurrency(amount);

  /* ------------------------------------------------------------------ */
  /* HELPERS                                                              */
  /* ------------------------------------------------------------------ */
  const buildApplyUrl = () => {
    if (targetType === 'retainer') return `${apiUrl}/api/retainers/${projectId}/${targetId}/apply-payment`;
    if (targetType === 'timebilling') return `${apiUrl}/api/timebilling-invoices/${targetId}/apply-payment`;
    if (targetType === 'invoice') return `${apiUrl}/api/invoices/${projectId}/${targetId}/apply-payment`;
    if (targetType === 'proposal') return `${apiUrl}/api/proposals/${projectId}/${targetId}/apply-payment`;
    if (targetType === 'purchase-order') return `${apiUrl}/api/purchase-orders/${projectId}/${targetId}/apply-payment`;
    throw new Error(`Invalid payment target: ${targetType}`);
  };

  // Connect-aware, document-specific create-payment-intent routes — these make
  // sure funds transfer to the designer's connected Stripe account instead of
  // sitting on the platform balance. Purchase orders pay a vendor rather than
  // collecting from a client, so Connect routing doesn't apply there; that one
  // still goes through the generic platform-only endpoint.
  const buildCreateIntentUrl = () => {
    if (targetType === 'retainer') return `${apiUrl}/api/retainers/${projectId}/${targetId}/create-payment-intent`;
    if (targetType === 'timebilling') return `${apiUrl}/api/timebilling-invoices/${targetId}/create-payment-intent`;
    if (targetType === 'invoice') return `${apiUrl}/api/invoices/${projectId}/${targetId}/create-payment-intent`;
    if (targetType === 'proposal') return `${apiUrl}/api/proposals/${projectId}/${targetId}/create-payment-intent`;
    if (targetType === 'purchase-order') return `${apiUrl}/api/payments/create-payment-intent`;
    throw new Error(`Invalid payment target: ${targetType}`);
  };

  const recordPayment = async (chargedAmount, intentId, method = 'Stripe') => {
    const applyRes = await fetch(buildApplyUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(effectiveUserId ? { userid: effectiveUserId } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        userId: effectiveUserId,
        amount: chargedAmount,
        paymentType: 'stripe',
        method,
        reference: intentId,
        stripeIntentId: intentId,
      }),
    });
    const applyData = await applyRes.json().catch(() => ({}));
    if (!applyRes.ok) throw new Error(applyData?.error || 'Payment was captured, but recording it failed.');
  };

  const createPaymentIntent = async (chargedAmount, pmType = 'card') => {
    const isPurchaseOrder = targetType === 'purchase-order';
    const res = await fetch(buildCreateIntentUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(effectiveUserId ? { userid: effectiveUserId } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        amount: chargedAmount,
        paymentMethodType: pmType,
        // Only the legacy platform-only PO endpoint needs these — the
        // Connect-aware routes derive description/metadata server-side.
        ...(isPurchaseOrder ? { description, metadata: { projectId, targetType, targetId } } : {}),
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.clientSecret) throw new Error(data?.error || 'Missing Stripe client secret');
    return data.clientSecret;
  };

  const validateAmount = (val) => {
    const amt = roundCurrency(val);
    if (!amt || amt < 1) { setError('Amount must be at least $1'); return null; }
    if (amt > remainingBalance) { setError(`Amount cannot exceed $${remainingBalance}`); return null; }
    return amt;
  };

  /* ------------------------------------------------------------------ */
  /* CARD FLOW                                                            */
  /* ------------------------------------------------------------------ */
  const handleCardSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    const amountToCharge = validateAmount(customAmount);
    if (!amountToCharge) return;

    setLoading(true);
    setError('');

    try {
      const clientSecret = await createPaymentIntent(amountToCharge, 'card');
      const card = elements.getElement(CardElement);
      const result = await stripe.confirmCardPayment(clientSecret, { payment_method: { card } });
      if (result.error) { setError(result.error.message); return; }
      await recordPayment(amountToCharge, result.paymentIntent.id, 'Stripe');
      setSuccess(true);
      onSuccess(amountToCharge, result.paymentIntent.id);
    } catch (err) {
      setError(err?.message || 'Payment failed. Please try again.');
    }

    setLoading(false);
  };

  /* ------------------------------------------------------------------ */
  /* ACH FLOW — STEP 1: COLLECT BANK ACCOUNT                            */
  /* ------------------------------------------------------------------ */
  const handleAchCollect = async (e) => {
    e.preventDefault();
    if (!stripe) return;

    const amountToCharge = validateAmount(customAmount);
    if (!amountToCharge) return;
    if (!achName.trim()) { setError('Account holder name is required'); return; }
    if (!achEmail.trim()) { setError('Email address is required'); return; }

    setLoading(true);
    setError('');

    try {
      const clientSecret = await createPaymentIntent(amountToCharge, 'us_bank_account');

      const { paymentIntent, error: collectError } = await stripe.collectBankAccountForPayment({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: { name: achName.trim(), email: achEmail.trim() },
          },
        },
      });

      if (collectError) { setError(collectError.message); return; }

      if (paymentIntent?.status === 'requires_confirmation') {
        setPendingClientSecret(clientSecret);
        setPendingIntentId(paymentIntent.id);
        setAchStep('mandate');
      } else {
        setError('Bank account connection was cancelled. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to connect bank account. Please try again.');
    }

    setLoading(false);
  };

  /* ------------------------------------------------------------------ */
  /* ACH FLOW — STEP 2: CONFIRM WITH MANDATE ACCEPTANCE                 */
  /* ------------------------------------------------------------------ */
  const handleAchConfirm = async () => {
    if (!stripe || !pendingClientSecret) return;
    setLoading(true);
    setError('');

    try {
      const { paymentIntent, error: confirmError } = await stripe.confirmUsBankAccountPayment(pendingClientSecret);

      if (confirmError) {
        if (confirmError.payment_intent?.status === 'requires_payment_method') {
          setAchStep('input');
          setError('Bank account verification failed. Please try a different account.');
        } else {
          setError(confirmError.message);
        }
        return;
      }

      const amountToCharge = roundCurrency(customAmount);
      const intentId = paymentIntent?.id || pendingIntentId;

      // ACH debits confirm to "processing", not "succeeded" — they settle
      // 3-5 business days later, so the backend won't accept a synchronous
      // apply-payment call yet. The payment_intent.succeeded webhook credits
      // the document once the debit actually clears.
      if (paymentIntent?.status === 'succeeded') {
        await recordPayment(amountToCharge, intentId, 'ACH');
      } else if (paymentIntent?.status !== 'processing') {
        setError('Bank account connection was cancelled. Please try again.');
        setLoading(false);
        return;
      }

      setSuccess(true);
      onSuccess(amountToCharge, intentId);
    } catch (err) {
      setError(err?.message || 'Payment confirmation failed. Please try again.');
    }

    setLoading(false);
  };

  const switchMethod = (method) => {
    setPaymentMethod(method);
    setError('');
    setAchStep('input');
    setPendingClientSecret('');
  };

  /* ------------------------------------------------------------------ */
  /* RENDER                                                               */
  /* ------------------------------------------------------------------ */
  return (
    <div className="spm-overlay payment-popup-overlay">
      <div className="spm-modal payment-popup-panel">
        <h2>Make a Payment</h2>

        {success ? (
          <div className="spm-success">
            <p>
              {paymentMethod === 'ach'
                ? 'ACH payment initiated. Funds typically settle in 3–5 business days.'
                : 'Payment Successful'}
            </p>
            <button onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            {/* Method Tabs */}
            <div className="spm-method-tabs">
              <button
                type="button"
                className={`spm-method-tab${paymentMethod === 'card' ? ' active' : ''}`}
                onClick={() => switchMethod('card')}
              >
                Card
              </button>
              <button
                type="button"
                className={`spm-method-tab${paymentMethod === 'ach' ? ' active' : ''}`}
                onClick={() => switchMethod('ach')}
              >
                ACH Bank Transfer
              </button>
            </div>

            {/* CARD FORM */}
            {paymentMethod === 'card' && (
              <form onSubmit={handleCardSubmit}>
                <div className="spm-field">
                  <label>Payment Amount</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    max={remainingBalance}
                    placeholder={`Max: $${remainingBalance}`}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(sanitizeCurrencyInput(e.target.value))}
                    className="spm-amount-input"
                  />
                </div>
                <div className="spm-field">
                  <label>Card Information</label>
                  <CardElement className="spm-card-element" />
                </div>
                {error && <p className="spm-error">{error}</p>}
                <button type="submit" className="spm-pay-btn" disabled={!stripe || loading}>
                  {loading ? 'Processing…' : 'Submit Payment'}
                </button>
                <button type="button" className="spm-cancel" onClick={onClose}>Cancel</button>
              </form>
            )}

            {/* ACH FORM — STEP 1 */}
            {paymentMethod === 'ach' && achStep === 'input' && (
              <form onSubmit={handleAchCollect}>
                <div className="spm-field">
                  <label>Payment Amount</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    max={remainingBalance}
                    placeholder={`Max: $${remainingBalance}`}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(sanitizeCurrencyInput(e.target.value))}
                    className="spm-amount-input"
                  />
                </div>
                <div className="spm-field">
                  <label>Account Holder Name</label>
                  <input
                    type="text"
                    placeholder="Full name"
                    value={achName}
                    onChange={(e) => setAchName(e.target.value)}
                    className="spm-amount-input"
                    required
                  />
                </div>
                <div className="spm-field">
                  <label>Email Address</label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={achEmail}
                    onChange={(e) => setAchEmail(e.target.value)}
                    className="spm-amount-input"
                    required
                  />
                </div>
                <p className="spm-ach-note">
                  You'll be prompted to securely connect your bank account via Stripe.
                </p>
                {error && <p className="spm-error">{error}</p>}
                <button type="submit" className="spm-pay-btn" disabled={!stripe || loading}>
                  {loading ? 'Connecting…' : 'Connect Bank Account'}
                </button>
                <button type="button" className="spm-cancel" onClick={onClose}>Cancel</button>
              </form>
            )}

            {/* ACH FORM — STEP 2: MANDATE */}
            {paymentMethod === 'ach' && achStep === 'mandate' && (
              <div>
                <div className="spm-ach-mandate">
                  <p>
                    By clicking <strong>Confirm Payment</strong>, you authorize the debit of your
                    bank account via the ACH network for{' '}
                    <strong>${roundCurrency(customAmount)}</strong>. This authorization remains
                    in effect until revoked with 30 days' written notice. You may be contacted
                    if any issues arise with your payment.
                  </p>
                </div>
                {error && <p className="spm-error">{error}</p>}
                <button
                  type="button"
                  className="spm-pay-btn"
                  onClick={handleAchConfirm}
                  disabled={loading}
                >
                  {loading ? 'Confirming…' : 'Confirm Payment'}
                </button>
                <button
                  type="button"
                  className="spm-cancel"
                  onClick={() => { setAchStep('input'); setPendingClientSecret(''); }}
                >
                  Back
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
